import Phaser from 'phaser';

import { TwentyOneMainScene } from '../../scenes/twentyone-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { UxMetrics } from '../../model/ux-metrics';
import { LoadingColumnViewModel } from './loading-column-view-model';
import { Positioning } from '../../model/positioning';
import { AvailableFontSizes, TextService } from '../../services/text-service';
// import { DebugContainerVisual } from '../debug/debug-container-visual';

export class LoadingColumnView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private scoreImage: Phaser.GameObjects.Image;
  private scoreLabel: Phaser.GameObjects.Text;

  private fadeValue = 0;
  private destroyTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    scene: TwentyOneMainScene,
    x: number,
    y: number,
    private viewModel: LoadingColumnViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.scoreImage = scene.add
      .image(0, 0, viewModel.scoreBoxAsset.type)
      .setOrigin(0.5, 0.5)
      .setAlpha(0);
    this.add(this.scoreImage);

    const fontStyle = TextService.fontStyleBold(
      AvailableFontSizes.XLarge,
      this.viewModel.labelColor,
      this.viewModel.defaultFontFamily,
    );

    this.scoreLabel = this.scene.add.text(0, 0, '21', fontStyle).setOrigin(0.5, 0.5).setAlpha(0);
    this.add(this.scoreLabel);

    this.setName(LoadingColumnView.name);
    scene.add.existing(this);

    this.showLoadingView();

    this.subscriptionGroup.add(
      this.viewModel.onGameSetupStart$.subscribe(() => {
        this.hideLoadingView();
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onResetGameScene$.subscribe(() => {
        this.showLoadingView();
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    this.destroyTweens.forEach((tween) => {
      tween?.stop();
    });

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.currentResizeMetrics = resizeMetrics;

    this.refreshLayout(resizeMetrics);
  }

  private showLoadingView(): void {
    const loadingTween = this.scene.tweens.add({
      targets: this,
      fadeValue: 1,
      duration: UxMetrics.LoadingFade,
      ease: Phaser.Math.Easing.Quadratic.InOut,
      onUpdate: (_tween) => {
        this.setupProgress(this.fadeValue);
      },
    });
    this.destroyTweens.push(loadingTween);

    const flashTween = this.scene.tweens.add({
      targets: this.scoreLabel,
      alpha: 0,
      duration: UxMetrics.LoadingScoreDuration,
      delay: UxMetrics.LoadingFade + UxMetrics.LoadingScoreDelay * (this.viewModel.columnId - 1),
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1, // Repeat indefinitely
      repeatDelay: UxMetrics.LoadingScoreRepeat, // Wait between flashes
    });
    this.destroyTweens.push(flashTween);
  }

  private hideLoadingView(): void {
    this.destroyTweens.forEach((tween) => {
      tween?.stop();
    });
    this.setupProgress(0);
  }

  private setupProgress(progress: number): void {
    this.scoreImage.setAlpha(progress);
    this.scoreLabel.setAlpha(progress);
  }

  private refreshLayout(resizeMetrics: ResizeMetrics): void {
    const columnRect = Positioning.globalColumnRect(
      this.viewModel.columnIndex,
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );

    this.setSize(columnRect.width, columnRect.height);
    this.setPosition(columnRect.pos.x, columnRect.pos.y);

    const columnScoreRect = Positioning.localColumnScoreRect(
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );
    this.scoreImage.setPosition(columnScoreRect.pos.x, columnScoreRect.pos.y);
    this.scoreImage.setDisplaySize(columnScoreRect.width, columnScoreRect.height);

    this.scoreLabel.setFontSize(resizeMetrics.fontMetrics.XLarge);
    const padding = resizeMetrics.layoutConfig.paddingSm * resizeMetrics.scalingMetrics.scaleFactor;
    this.scoreLabel.setSize(this.scoreLabel.width + padding * 2, this.scoreLabel.height + padding);
    this.scoreLabel.setPosition(columnScoreRect.pos.x, columnScoreRect.pos.y);
  }
}
